import { router } from "@inertiajs/react";
import { format } from "date-fns";
import { ChevronDownIcon, ExternalLinkIcon, ReceiptIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import { CopyButton } from "@/components/shared/copy-button";
import { Disclosure } from "@/components/shared/disclosure";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PAYMENT_STATUS_STYLES } from "@/lib/orders/status-styles";
import { cn, formatCurrency } from "@/lib/utils";
import {
	canGenerateInvoice,
	hasLiveLink,
	isRefund,
	type OrderPayment,
	SettlePaymentActions,
} from "./settle-payments";

// The row shape lives with the actions that close it; re-exported here because the
// orders page has always imported it from this panel.
export type { OrderPayment };

// The order-level money this panel accounts for. The bill and the payments that
// answer it used to be two sections with two headings and no shared number
// between them; they are one story and now they are one section.
export interface PaymentsPanelOrder {
	id: string;
	totalAmount: number;
	paidAmount: number;
	remainingAmount: number;
	paymentStatus: string;
	packageBasePrice: number;
	discountType: string | null;
	discountAmount: number;
	taxPercentage: number;
	taxAmount: number;
	voucherCode: string | null;
	invoiceNumber: string | null;
	invoiceDueDate: string | null;
	payments: OrderPayment[];
}

const PAYMENT_BADGE_STYLES: Record<string, string> = {
	COMPLETED: "bg-emerald-100 text-emerald-700 border-emerald-200",
	PENDING: "bg-amber-100 text-amber-700 border-amber-200",
	PENDING_REFUND: "bg-orange-100 text-orange-700 border-orange-200",
	FAILED: "bg-red-100 text-red-700 border-red-200",
	EXPIRED: "bg-muted text-muted-foreground border-border",
	CANCELLED: "bg-muted text-muted-foreground border-border",
};

// The date that says when this row's money actually moved. A settled row is dated
// by its settlement; one still waiting is dated by when the obligation was born.
const paymentDate = (payment: OrderPayment) =>
	payment.paidAt || payment.refundedAt || payment.createdAt;

// How many rows the ledger shows before it folds. Four covers the common shape —
// the original payment plus a change or two — so the fold appears only on the
// orders that genuinely have a history.
const TIMELINE_PREVIEW = 4;

export function PaymentsPanel({ order }: { order: PaymentsPanelOrder }) {
	const { t } = useTranslation("appointments", { useSuspense: false });
	const { locale: dateFnsLocale } = useDateContext();
	const [showAllPayments, setShowAllPayments] = useState(false);
	const showVoucherRow = order.discountAmount > 0 || !!order.voucherCode;

	// An order that was re-billed a few times can carry a long ledger, and the panel
	// sits inside a dialog. Cut it to the newest few — plus every row still waiting
	// on an admin, whatever its age, because those are the reason to open this panel
	// at all. `hiddenCount` is measured against the cut list, never the shown one, so
	// the toggle can still say what it folds while it is open.
	const collapsedPayments = order.payments.filter(
		(payment, index) => index < TIMELINE_PREVIEW || payment.settleable,
	);
	const hiddenCount = order.payments.length - collapsedPayments.length;
	const visiblePayments = showAllPayments ? order.payments : collapsedPayments;

	const reload = () =>
		router.reload({ only: ["selectedOrder", "orders", "flash", "errors"] });

	return (
		<div>
			<h4 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
				{t("payments.title")}
			</h4>

			{/* The three numbers first, because the rows below are the working of
			    exactly this sum. `paidAmount` was missing from the old breakdown, which
			    is what left the payment list looking like an unrelated second section. */}
			<div className="rounded-lg border divide-y sm:grid sm:grid-cols-3 sm:divide-y-0 sm:divide-x">
				<Figure
					label={t("payments.summary.total")}
					value={formatCurrency(order.totalAmount)}
				/>
				<Figure
					label={t("payments.summary.paid")}
					value={formatCurrency(order.paidAmount)}
				/>
				<Figure
					label={t("payments.summary.remaining")}
					value={formatCurrency(order.remainingAmount)}
					valueClassName={order.remainingAmount > 0 ? "text-amber-700" : ""}
					trailing={
						<Badge
							className={cn(
								"text-[10px] border p-0 px-1 rounded-full font-bold",
								PAYMENT_STATUS_STYLES[order.paymentStatus] ||
									"bg-background hover:bg-background/80",
							)}
						>
							{order.paymentStatus.replace(/_/g, " ")}
						</Badge>
					}
				/>
			</div>

			{/* How the total was arrived at. Folded away by default: it changes only
			    when the package or the voucher changes, while the rows below change
			    every time money moves. */}
			<Disclosure
				className="mt-2"
				contentClassName="px-0 py-0"
				icon={ReceiptIcon}
				title={t("payments.bill.header")}
				hint={t("payments.bill.hint")}
			>
				<table className="w-full text-sm">
					<tbody>
						{order.packageBasePrice > 0 && (
							<BillRow
								label={t("payments.bill.base_price")}
								value={formatCurrency(order.packageBasePrice)}
							/>
						)}
						{showVoucherRow && (
							<BillRow
								label={
									<>
										{t("payments.bill.discount")}
										{order.discountType && (
											<span className="text-xs"> ({order.discountType})</span>
										)}
										{order.voucherCode && (
											<a
												href={`/admin-portal/vouchers?code=${order.voucherCode}`}
												target="_blank"
												rel="noreferrer"
												className="ml-1.5 inline-flex items-center gap-1 text-blue-600 hover:underline text-xs font-semibold"
											>
												{order.voucherCode}
												<ExternalLinkIcon className="size-3" />
											</a>
										)}
									</>
								}
								value={`-${formatCurrency(order.discountAmount)}`}
								valueClassName="text-red-600"
							/>
						)}
						{order.taxAmount > 0 && (
							<BillRow
								label={`${t("payments.bill.tax")} (${order.taxPercentage}%)`}
								value={formatCurrency(order.taxAmount)}
							/>
						)}
						<tr className="bg-muted/50 font-semibold">
							<td className="px-3 py-2">{t("payments.bill.total")}</td>
							<td className="px-3 py-2 text-right tabular-nums">
								{formatCurrency(order.totalAmount)}
							</td>
						</tr>
						{order.invoiceNumber && (
							<BillRow
								label={t("payments.bill.invoice_number")}
								value={
									<span className="inline-flex items-center gap-1.5">
										<span className="select-all font-mono">
											{order.invoiceNumber}
										</span>
										<CopyButton
											value={order.invoiceNumber}
											label={`${t("payments.bill.invoice_number")}: ${order.invoiceNumber}`}
										/>
									</span>
								}
							/>
						)}
						{order.invoiceDueDate && (
							<BillRow
								label={t("payments.bill.due_date")}
								value={format(new Date(order.invoiceDueDate), "d MMM yyyy", {
									locale: dateFnsLocale,
								})}
							/>
						)}
					</tbody>
				</table>
			</Disclosure>

			{/* Newest first, dated in the left rail: this is a ledger, and the question
			    it answers is "when did the money move". */}
			{order.payments.length > 0 && (
				<>
					<div className="mt-4 mb-2 flex items-baseline justify-between gap-2">
						<h5 className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
							{t("payments.timeline.label")}
						</h5>
						{/* The count is stated rather than left to be counted, because the
						    list below may be cut and a reader has to know the whole of it. */}
						<span className="text-[11px] tabular-nums text-muted-foreground">
							{t("payments.timeline.count", { count: order.payments.length })}
						</span>
					</div>

					<ol className="space-y-3">
						{visiblePayments.map((payment) => (
							<li key={payment.id} className="flex gap-3">
								<time
									dateTime={paymentDate(payment)}
									className="w-14 shrink-0 pt-0.5 text-xs tabular-nums text-muted-foreground"
								>
									{format(new Date(paymentDate(payment)), "d MMM", {
										locale: dateFnsLocale,
									})}
								</time>

								<div
									className={cn(
										"min-w-0 flex-1 border-l pl-3 pb-1",
										payment.settleable && "border-l-2 border-l-amber-500/70",
									)}
								>
									<div className="flex items-baseline justify-between gap-2">
										<span className="text-sm font-medium">
											{t(`payments.types.${payment.paymentType}`, {
												defaultValue: payment.paymentType.replace(/_/g, " "),
											})}
										</span>
										<span className="text-sm font-semibold tabular-nums shrink-0">
											{isRefund(payment) ? "−" : ""}
											{formatCurrency(payment.amount)}
										</span>
									</div>

									<div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
										<Badge
											className={cn(
												"text-[10px] border p-0 px-1 rounded-full font-bold",
												PAYMENT_BADGE_STYLES[payment.status] ||
													"bg-background hover:bg-background/80",
											)}
										>
											{payment.status.replace(/_/g, " ")}
										</Badge>
										<span className="text-xs text-muted-foreground">
											{[payment.paymentMethod, payment.paymentGateway]
												.filter(Boolean)
												.join(" · ")}
										</span>
										{hasLiveLink(payment) && payment.invoiceUrl ? (
											<>
												<a
													href={payment.invoiceUrl}
													target="_blank"
													rel="noreferrer"
													className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs font-semibold"
												>
													{t("payments.actions.open_invoice")}
													<ExternalLinkIcon className="size-3" />
												</a>
												<CopyButton
													value={payment.invoiceUrl}
													label={t("payments.actions.copy_invoice_url")}
													className="[&_svg]:size-3"
												/>
											</>
										) : (
											canGenerateInvoice(payment) && (
												<span className="text-xs text-muted-foreground/75">
													{t(
														payment.linkExpired
															? "payments.link_expired"
															: "payments.no_link",
													)}
												</span>
											)
										)}
									</div>

									{payment.description && (
										<p className="text-xs text-muted-foreground mt-1 break-words">
											{payment.description}
										</p>
									)}

									{payment.settleable && (
										<SettlePaymentActions
											orderId={order.id}
											payment={payment}
											onDone={reload}
										/>
									)}
								</div>
							</li>
						))}
					</ol>

					{/* Nothing is thrown away — the cut is a fold, and the count on it says
					    exactly how much is folded. Rows still waiting for an admin never
					    fall inside the fold, however old they are. */}
					{hiddenCount > 0 && (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="mt-2 h-8 w-full border border-dashed text-xs text-muted-foreground hover:text-foreground"
							onClick={() => setShowAllPayments(!showAllPayments)}
						>
							{showAllPayments
								? t("payments.timeline.collapse")
								: t("payments.timeline.show_all", { count: hiddenCount })}
							<ChevronDownIcon
								className={cn(
									"ml-1 size-3.5 transition-transform duration-200",
									showAllPayments && "rotate-180",
								)}
							/>
						</Button>
					)}
				</>
			)}
		</div>
	);
}

function Figure({
	label,
	value,
	valueClassName,
	trailing,
}: {
	label: string;
	value: string;
	valueClassName?: string;
	trailing?: ReactNode;
}) {
	return (
		<div className="px-3 py-2">
			<p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
				{label}
			</p>
			<div className="flex items-center justify-between gap-2">
				<span
					className={cn("text-sm font-semibold tabular-nums", valueClassName)}
				>
					{value}
				</span>
				{trailing}
			</div>
		</div>
	);
}

function BillRow({
	label,
	value,
	valueClassName,
}: {
	label: ReactNode;
	value: ReactNode;
	valueClassName?: string;
}) {
	return (
		<tr className="border-b last:border-b-0">
			<td className="px-3 py-1.5 text-muted-foreground">{label}</td>
			<td
				className={cn(
					"px-3 py-1.5 text-right font-medium tabular-nums",
					valueClassName,
				)}
			>
				{value}
			</td>
		</tr>
	);
}
